import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authStorage } from 'functions/auth-session';
import {
    ACTION_WARNING_MINUTES, NOTIFICATIONS_CHANGED, actionsToNotify, markNotified, notificationsEnabled,
    readSince, wasNotified, writeSince,
} from 'functions/browser-notifications';
import { NotificationsService } from 'services/notifications.service';
import { CrmScheduleService } from '@layouts/pages/CRM/kanban-board/services/crm-schedule.service';

/// Um minuto: lead novo pede resposta rápida, mas perguntar a cada segundo sobrecarrega o servidor.
const INTERVAL_MS = 60 * 1000;

/// Avisa lead novo e ação perto da hora; roda uma vez no App, enquanto há sessão.
export const useBrowserNotifications = (active: boolean) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [enabled, setEnabled] = useState(notificationsEnabled);

    useEffect(() => {
        const refresh = () => setEnabled(notificationsEnabled());

        window.addEventListener(NOTIFICATIONS_CHANGED, refresh);
        window.addEventListener('storage', refresh);

        return () => {
            window.removeEventListener(NOTIFICATIONS_CHANGED, refresh);
            window.removeEventListener('storage', refresh);
        };
    }, []);

    useEffect(() => {
        if (!active || !enabled) return;

        const show = (title: string, body: string, tag: string, to: string) => {
            // A tag troca o aviso repetido em vez de empilhar quando duas abas perguntam juntas.
            const notification = new Notification(title, { body, tag });

            notification.onclick = () => {
                window.focus();
                navigate(to);
                notification.close();
            };
        };

        const check = async () => {
            const now = new Date();

            try {
                const since = readSince(now);
                const res = await new NotificationsService().newLeads(since);
                const leads = res.data.data ?? [];

                leads.forEach((lead) => show(
                    t('notificacoes.leadNovo'),
                    lead.sourceCampaign ? `${lead.name} · ${lead.sourceCampaign}` : lead.name,
                    `lead-${lead.id}`,
                    `/crm/lead/${lead.id}`,
                ));

                writeSince(leads.length > 0 ? leads[leads.length - 1].createdAt : now.toISOString());
            } catch {
                // Aviso é acessório: falha aqui fica para a próxima rodada.
            }

            try {
                const me = authStorage.read()?.userId;

                if (!me) return;

                const until = new Date(now.getTime() + ACTION_WARNING_MINUTES * 60 * 1000);
                const res = await new CrmScheduleService().agenda(now.toISOString(), until.toISOString(), me);

                actionsToNotify(res.data.data ?? [], now, wasNotified).forEach((item) => {
                    const minutes = Math.max(0, Math.round((new Date(item.scheduledAt).getTime() - now.getTime()) / 60000));

                    show(
                        t('notificacoes.acaoEm', { minutos: minutes }),
                        `${item.description} · ${item.leadName}`,
                        `acao-${item.id}`,
                        `/crm/lead/${item.leadId}`,
                    );
                    markNotified(item.id);
                });
            } catch {
                // Idem.
            }
        };

        check();
        const timer = setInterval(check, INTERVAL_MS);

        return () => clearInterval(timer);
    }, [active, enabled, navigate, t]);
};
